/**
 * Lettura di un documento da una foto: codici a barre e MRZ.
 *
 * Qui c'e' solo la logica: come preparare l'immagine, dove cercare la MRZ e
 * in che ordine provare. OCR, lettura dei codici a barre e canvas li fornisce
 * chi chiama (l'"ambiente"), cosi' la stessa logica gira nel browser e nei
 * test.
 *
 * L'ambiente deve avere:
 *  - canvas(larghezza, altezza): un canvas con getContext("2d")
 *  - ocr(canvas, { psm }): Promise di [{ testo, x0, y0, x1, y1 }], una per riga
 *    (modello della MRZ: solo lettere maiuscole, cifre e "<")
 *  - ocrTesto(canvas, { psm }): come ocr, ma con il modello per il testo
 *    stampato (patenti); si carica solo se serve
 *  - entrambi accettano anche { soglia: "adattiva" }: il bianco e nero
 *    dell'OCR si fa per zone (Sauvola) invece che su tutta l'immagine (Otsu)
 *  - barcode(canvas): Promise di [{ formato, testo }]
 *  - avanzamento(messaggio, da, a) facoltativo, per mostrare a che punto si
 *    e': la fase in corso e il tratto di percentuale stimata che occupa
 *  - sfocata(nitidezza) facoltativo: chiamata subito, prima della lettura,
 *    se la foto e' troppo sfocata (vedi NITIDEZZA_MINIMA)
 */

import { leggiMrz, pulisciRiga, sembraMrz } from "./mrz";
import { trovaCodiceFiscale, codiceCognome, codiceNome } from "./codicefiscale";
import { leggiAamva } from "./aamva";
import { leggiPatenteUe } from "./patente";
import { leggiViz, unisciViz } from "./viz";

/** Lato massimo dell'immagine su cui si lavora: oltre si rallenta e basta. */
const LATO_MASSIMO = 2000;
/** Lato della copia usata per trovare le righe della MRZ. */
const LATO_RICERCA = 1600;
/**
 * Nitidezza sotto la quale la lettura comincia a perdere dati (vedi
 * nitidezza()). Tarata sfocando a poco a poco foto reali della CIE, fronte e
 * retro: nitide 41-42; sfocate di 1 pixel 27-28, si legge tutto; di 1,5
 * pixel 19-20, il fronte perde il cognome; di 2 pixel 13-14, non si legge
 * nulla. Da rivedere con i valori delle foto vere (console del browser).
 */
const NITIDEZZA_MINIMA = 22;
/** Altezza in pixel delle righe MRZ nella seconda lettura, quella precisa. */
const ALTEZZA_RIGA = 44;

// ___________________________ IMMAGINE ___________________________

/** Copia `sorgente` scalata in modo che il lato maggiore sia al massimo `lato`. */
export function scala(amb, sorgente, lato, ingrandisci = false) {
  const w = sorgente.width;
  const h = sorgente.height;
  let f = lato / Math.max(w, h);
  if (f > 1 && !ingrandisci) {
    f = 1;
  }
  const c = amb.canvas(Math.round(w * f), Math.round(h * f));
  const ctx = c.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(sorgente, 0, 0, c.width, c.height);
  return c;
}

/** Copia ruotata di 90, 180 o 270 gradi. */
export function ruota(amb, sorgente, gradi) {
  const dritta = gradi % 180 === 0;
  const c = amb.canvas(dritta ? sorgente.width : sorgente.height, dritta ? sorgente.height : sorgente.width);
  const ctx = c.getContext("2d");
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate((gradi * Math.PI) / 180);
  ctx.drawImage(sorgente, -sorgente.width / 2, -sorgente.height / 2);
  return c;
}

/** Ritaglio di `sorgente`, ingrandito o rimpicciolito di `fattore`. */
function ritaglia(amb, sorgente, r, fattore) {
  const c = amb.canvas(Math.max(1, Math.round(r.w * fattore)), Math.max(1, Math.round(r.h * fattore)));
  const ctx = c.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(sorgente, r.x, r.y, r.w, r.h, 0, 0, c.width, c.height);
  return c;
}

/**
 * Scala di grigi con contrasto allargato: i fondini colorati e le foto un po'
 * scure dei documenti disturbano meno l'OCR.
 */
export function grigio(canvas) {
  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  const istogramma = new Array(256).fill(0);
  for (let i = 0; i < d.length; i += 4) {
    const g = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
    d[i] = g;
    istogramma[g]++;
  }
  // Si taglia l'1% piu' scuro e l'1% piu' chiaro e si allarga il resto.
  const n = d.length / 4;
  let basso = 0;
  let alto = 255;
  for (let s = 0; basso < 255 && s + istogramma[basso] < n * 0.01; basso++) {
    s += istogramma[basso];
  }
  for (let s = 0; alto > 0 && s + istogramma[alto] < n * 0.01; alto--) {
    s += istogramma[alto];
  }
  const ampiezza = Math.max(1, alto - basso);
  for (let i = 0; i < d.length; i += 4) {
    const g = Math.max(0, Math.min(255, Math.round(((d[i] - basso) * 255) / ampiezza)));
    d[i] = d[i + 1] = d[i + 2] = g;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/** Bianco e nero con soglia di Otsu: per la seconda lettura della sola MRZ. */
export function binarizza(canvas) {
  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  const istogramma = new Array(256).fill(0);
  for (let i = 0; i < d.length; i += 4) {
    istogramma[d[i]]++;
  }
  const n = d.length / 4;
  let somma = 0;
  for (let t = 0; t < 256; t++) {
    somma += t * istogramma[t];
  }
  let sommaB = 0;
  let pesoB = 0;
  let migliore = 0;
  let soglia = 128;
  for (let t = 0; t < 256; t++) {
    pesoB += istogramma[t];
    if (!pesoB) {
      continue;
    }
    const pesoF = n - pesoB;
    if (!pesoF) {
      break;
    }
    sommaB += t * istogramma[t];
    const mB = sommaB / pesoB;
    const mF = (somma - sommaB) / pesoF;
    const varianza = pesoB * pesoF * (mB - mF) * (mB - mF);
    if (varianza > migliore) {
      migliore = varianza;
      soglia = t;
    }
  }
  for (let i = 0; i < d.length; i += 4) {
    const v = d[i] > soglia ? 255 : 0;
    d[i] = d[i + 1] = d[i + 2] = v;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/**
 * Toglie da una riga in bianco e nero i bordi del documento ai lati.
 *
 * Il bordo della tessera o l'ombra del bordo sono tratti verticali sottili,
 * staccati dal testo: l'OCR li legge come "I" o "1" in testa o in coda alla
 * riga. Un tratto all'estremita' piu' stretto di mezzo carattere e lontano dal
 * testo piu' di un carattere e mezzo si cancella.
 */
export function pulisciBordi(canvas, larghezzaCarattere) {
  const ctx = canvas.getContext("2d");
  const { width: w, height: h } = canvas;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const inchiostro = new Array(w).fill(false);
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      if (d[(y * w + x) * 4] < 128) {
        inchiostro[x] = true;
        break;
      }
    }
  }
  // Tratti continui di colonne con inchiostro.
  const tratti = [];
  for (let x = 0; x < w; x++) {
    if (inchiostro[x] && (x === 0 || !inchiostro[x - 1])) {
      tratti.push({ da: x, a: x });
    }
    if (inchiostro[x]) {
      tratti[tratti.length - 1].a = x;
    }
  }
  const sottile = (t) => t.a - t.da + 1 < larghezzaCarattere * 0.5;
  const cancella = (t) => {
    for (let x = t.da; x <= t.a; x++) {
      for (let y = 0; y < h; y++) {
        const i = (y * w + x) * 4;
        d[i] = d[i + 1] = d[i + 2] = 255;
      }
    }
  };
  while (tratti.length > 1 && sottile(tratti[0]) && tratti[1].da - tratti[0].a > larghezzaCarattere * 1.5) {
    cancella(tratti.shift());
  }
  while (tratti.length > 1 && sottile(tratti[tratti.length - 1]) && tratti[tratti.length - 1].da - tratti[tratti.length - 2].a > larghezzaCarattere * 1.5) {
    cancella(tratti.pop());
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

// ___________________________ MRZ ___________________________

/**
 * Righe dell'OCR che possono essere della MRZ, anche monche.
 *
 * Solo righe orizzontali: con il documento fotografato in verticale l'OCR a
 * volte legge lo stesso il testo girato, ma la rilettura precisa lavora per
 * righe orizzontali. Si prova allora la foto ruotata di 90 gradi.
 */
function righeMrz(righe) {
  return righe.filter((r) => {
    const t = pulisciRiga(r.testo);
    const minori = (t.match(/</g) || []).length;
    const cifre = (t.match(/[0-9]/g) || []).length;
    // Le righe della MRZ hanno molti "<" o, la seconda, molte cifre: una
    // riga di testo normale con uno o due "<" letti per errore non conta.
    const forte = minori >= 4 || (minori >= 1 && cifre >= 6) || cifre >= 12;
    return r.x1 - r.x0 > (r.y1 - r.y0) * 2 && forte && sembraMrz(t, 20);
  });
}

/**
 * Pezzi di righe MRZ vicini a quelle trovate: con riflessi o fondini l'OCR
 * spezza una riga in piu' frammenti corti. Non bastano per leggere, ma dicono
 * dove stanno le righe e quanto distano una dall'altra.
 */
function frammentiMrz(righe, candidate) {
  const altezza = mediana(candidate.map((r) => r.y1 - r.y0));
  const y0 = Math.min(...candidate.map((r) => r.y0)) - altezza * 4;
  const y1 = Math.max(...candidate.map((r) => r.y1)) + altezza * 4;
  return righe.filter((r) => {
    const t = pulisciRiga(r.testo);
    const cifre = (t.match(/[0-9]/g) || []).length;
    return r.y0 >= y0 && r.y1 <= y1 && r.x1 - r.x0 > (r.y1 - r.y0) * 2 && t.length >= 8 && (t.includes("<") || cifre >= 6);
  });
}

/**
 * Versi in cui provare a leggere: i documenti sono orizzontali, quindi in una
 * foto verticale e' probabile che siano girati di 90 gradi, e si comincia da
 * li'. Si provano comunque tutti.
 */
function versi(img) {
  return img.height > img.width * 1.15 ? [90, 270, 0, 180] : [0, 180, 90, 270];
}

/** Punteggio di una lettura: quanti controlli supera. */
function punti(mrz) {
  return mrz ? Object.values(mrz.controlli).filter(Boolean).length : -1;
}

function mediana(valori) {
  const v = [...valori].sort((a, b) => a - b);
  return v[Math.floor(v.length / 2)];
}

/**
 * Seconda lettura, precisa: rilegge una per una le righe della MRZ.
 *
 * La prima ricerca sull'intero documento spesso spezza o accorcia le righe
 * (fondini, timbri, la foto accanto). Qui ogni riga si ritaglia per tutta la
 * larghezza del documento, si ingrandisce a un'altezza fissa, si porta in
 * bianco e nero e si legge come riga singola. Si prova anche una riga sopra
 * e una sotto quelle trovate: la ricerca puo' averne persa una.
 *
 * Con `altezzaRiga` e `soglia` ("adattiva": bianco e nero dell'OCR per zone,
 * al posto del nostro) si ottengono letture un po' diverse della stessa riga:
 * servono a correggere il nome (vedi correggiNomeMrz).
 */
async function rileggiRighe(amb, originale, candidate, fattoreRicerca, { altezzaRiga = ALTEZZA_RIGA, soglia } = {}) {
  const righe = [...candidate].sort((a, b) => a.y0 - b.y0);
  const altezza = mediana(righe.map((r) => r.y1 - r.y0)) / fattoreRicerca;
  const centri = righe.map((r) => (r.y0 + r.y1) / 2 / fattoreRicerca);
  const passi = centri.slice(1).map((c, i) => c - centri[i]).filter((p) => p > altezza * 1.1 && p < altezza * 3);
  const passo = passi.length ? mediana(passi) : altezza * 1.7;
  // Righe trovate, piu' una sopra e una sotto; quelle troppo vicine si fondono.
  const ys = [centri[0] - passo, ...centri, centri[centri.length - 1] + passo].filter((y) => y > altezza / 2 && y < originale.height - altezza / 2);
  const bande = ys.filter((y, i) => i === 0 || y - ys[i - 1] > altezza * 0.6);
  const fattore = Math.min(4, Math.max(0.5, altezzaRiga / altezza));
  // In larghezza: se la ricerca ha trovato righe quasi intere ci si limita a
  // quelle (piu' un margine), cosi' il bordo del documento non viene letto
  // come carattere; se le ha trovate monche si prende tutta la larghezza.
  let x0 = 0;
  let x1 = originale.width;
  const lunghezze = righe.map((r) => pulisciRiga(r.testo).length);
  if (Math.max(...lunghezze) >= 24) {
    const carattere = mediana(righe.map((r, i) => (r.x1 - r.x0) / Math.max(1, lunghezze[i]))) / fattoreRicerca;
    x0 = Math.max(0, Math.min(...righe.map((r) => r.x0)) / fattoreRicerca - carattere * 2);
    x1 = Math.min(originale.width, Math.max(...righe.map((r) => r.x1)) / fattoreRicerca + carattere * 2);
  }
  const testi = [];
  for (const y of bande) {
    const h = altezza * 1.5;
    const r = { x: Math.round(x0), y: Math.max(0, Math.round(y - h / 2)), w: Math.round(x1 - x0), h: Math.round(h) };
    r.h = Math.min(r.h, originale.height - r.y);
    // Nei caratteri OCR-B della MRZ la larghezza e' circa 0,7 volte l'altezza.
    const grigia = grigio(ritaglia(amb, originale, r, fattore));
    const banda = soglia === "adattiva" ? grigia : pulisciBordi(binarizza(grigia), altezzaRiga * 0.7);
    const letto = await amb.ocr(banda, { psm: 7, soglia });
    testi.push(letto.map((x) => x.testo).join(""));
  }
  return leggiMrz(testi);
}

/** Copia ruotata di un angolo piccolo (gradi), su fondo bianco, stesse misure. */
function inclina(amb, sorgente, gradi, sfondo = "#fff") {
  const c = amb.canvas(sorgente.width, sorgente.height);
  const ctx = c.getContext("2d");
  ctx.fillStyle = sfondo;
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate((gradi * Math.PI) / 180);
  ctx.drawImage(sorgente, -sorgente.width / 2, -sorgente.height / 2);
  return c;
}

/**
 * Raddrizza una zona di testo leggermente inclinata (foto fatta storta).
 *
 * Si prova una serie di piccoli angoli: in quello giusto le righe di testo
 * sono orizzontali, e il profilo orizzontale dell'inchiostro alterna righe
 * piene e righe vuote in modo netto (la somma dei salti tra righe vicine e'
 * massima). Il calcolo si fa su una copia piccola, per essere veloce.
 */
export function raddrizza(amb, zona) {
  const piccola = binarizza(grigio(scala(amb, zona, 500, true)));
  let migliore = 0;
  let puntiMigliori = -1;
  for (let gradi = -10; gradi <= 10; gradi += 0.5) {
    const punti = puntiRighe(inclina(amb, piccola, gradi));
    if (punti > puntiMigliori) {
      puntiMigliori = punti;
      migliore = gradi;
    }
  }
  return migliore ? inclina(amb, zona, migliore) : zona;
}

/**
 * Quanto sono nette le righe di testo orizzontali di un'immagine in bianco e
 * nero: il profilo orizzontale dell'inchiostro alterna righe piene e righe
 * vuote, e la somma dei salti tra righe vicine e' massima quando il testo e'
 * dritto.
 */
function puntiRighe(c) {
  const d = c.getContext("2d").getImageData(0, 0, c.width, c.height).data;
  let precedente = 0;
  let punti = 0;
  for (let y = 0; y < c.height; y++) {
    let riga = 0;
    for (let x = 0; x < c.width; x++) {
      if (d[(y * c.width + x) * 4] < 128) {
        riga++;
      }
    }
    punti += (riga - precedente) * (riga - precedente);
    precedente = riga;
  }
  return punti;
}

/**
 * Copia con una cornice nera attorno (8% del lato maggiore).
 *
 * Un documento chiaro su un fondo chiaro (scrivania o foglio bianco) non ha
 * nulla di davvero scuro: lo stiramento del contrasto di grigio() e la soglia
 * unica dell'OCR scuriscono allora fondini e bordi quanto le scritte, e la
 * MRZ si perde. Il nero della cornice fa da riferimento, come un fondo scuro
 * sotto il documento: sulle prove con fondo bianco la MRZ torna leggibile e
 * la lettura e' quasi due volte piu' veloce; con fondo scuro non cambia nulla.
 */
function cornice(amb, img) {
  const m = Math.round(Math.max(img.width, img.height) * 0.08);
  const c = amb.canvas(img.width + 2 * m, img.height + 2 * m);
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(img, m, m);
  return c;
}

/**
 * Nitidezza di una foto: quanto sono netti i bordi piu' netti.
 *
 * Su una copia in grigi a contrasto pieno, con il lato di 1200 pixel (il
 * documento quasi riempie la foto: la misura dipende poco dalla risoluzione
 * della fotocamera), si calcola il laplaciano e se ne prende il valore che
 * supera solo l'1% dei punti: sono i bordi delle lettere e del documento.
 * Una foto mossa o fuori fuoco li ha morbidi e il valore scende. Si guardano
 * i bordi piu' netti e non la media: il fondo, liscio o a trama, non conta.
 */
export function nitidezza(amb, img) {
  const c = grigio(scala(amb, img, 1200, true));
  const { width: w, height: h } = c;
  const d = c.getContext("2d").getImageData(0, 0, w, h).data;
  const istogramma = new Uint32Array(4 * 255 + 1);
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = (y * w + x) * 4;
      istogramma[Math.abs(4 * d[i] - d[i - 4] - d[i + 4] - d[i - w * 4] - d[i + w * 4])]++;
      n++;
    }
  }
  let somma = 0;
  for (let v = istogramma.length - 1; v > 0; v--) {
    somma += istogramma[v];
    if (somma >= n * 0.01) {
      return v;
    }
  }
  return 0;
}

/** Colore medio di un'immagine (la rimpicciolisce a un punto solo). */
function coloreMedio(amb, img) {
  const c = amb.canvas(1, 1);
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return "rgb(" + r + "," + g + "," + b + ")";
}

/**
 * Maschera del testo: nero solo dove un punto e' piu' scuro dei dintorni
 * (le lettere), bianco altrove. Le zone uniformi (la scrivania, il fondo del
 * documento) restano bianche, e non disturbano la stima dell'inclinazione
 * come farebbe il bianco e nero di tutta la foto.
 */
export function mascheraTesto(amb, img, lato) {
  const c = grigio(scala(amb, img, lato, true));
  const w = c.width;
  const h = c.height;
  const ctx = c.getContext("2d");
  const im = ctx.getImageData(0, 0, w, h);
  const d = im.data;
  // Somme cumulate: la media di un quadrato si calcola in tempo costante.
  const W = w + 1;
  const somme = new Float64Array(W * (h + 1));
  for (let y = 0; y < h; y++) {
    let riga = 0;
    for (let x = 0; x < w; x++) {
      riga += d[(y * w + x) * 4];
      somme[(y + 1) * W + x + 1] = somme[y * W + x + 1] + riga;
    }
  }
  const r = Math.max(4, Math.round(lato / 60));
  const out = new Uint8ClampedArray(w * h);
  for (let y = 0; y < h; y++) {
    const y0 = Math.max(0, y - r);
    const y1 = Math.min(h, y + r + 1);
    for (let x = 0; x < w; x++) {
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(w, x + r + 1);
      const media = (somme[y1 * W + x1] - somme[y0 * W + x1] - somme[y1 * W + x0] + somme[y0 * W + x0]) / ((y1 - y0) * (x1 - x0));
      out[y * w + x] = d[(y * w + x) * 4] < media - 18 ? 0 : 255;
    }
  }
  for (let i = 0; i < out.length; i++) {
    d[i * 4] = d[i * 4 + 1] = d[i * 4 + 2] = out[i];
  }
  ctx.putImageData(im, 0, 0);
  return c;
}

/**
 * Dove sta il documento nella foto, per ritagliarlo a piena risoluzione.
 *
 * Una foto del telefono si riduce a LATO_MASSIMO prima di leggerla: se il
 * documento occupa meta' della foto, i suoi caratteri diventano di pochi
 * pixel e l'OCR ne perde molti. Si cerca allora la zona con il testo (celle
 * della maschera del testo con abbastanza inchiostro), la si allarga un po' e
 * si ritaglia dalla foto originale. Gli intervalli vuoti piccoli (la foto del
 * titolare, gli spazi tra le righe) restano dentro; un fondo con molta
 * trama (legno, tovaglia) accende tutte le celle e allora non si ritaglia.
 *
 * @returns {{x, y, w, h}|null} zona in pixel della foto, null se non serve
 */
export function trovaDocumento(amb, img) {
  const maschera = mascheraTesto(amb, img, 700);
  const { width: w, height: h } = maschera;
  const d = maschera.getContext("2d").getImageData(0, 0, w, h).data;
  const CELLA = 10;
  const cw = Math.ceil(w / CELLA);
  const ch = Math.ceil(h / CELLA);
  const inchiostro = new Uint16Array(cw * ch);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (d[(y * w + x) * 4] < 128) {
        inchiostro[Math.floor(y / CELLA) * cw + Math.floor(x / CELLA)]++;
      }
    }
  }
  // Cella di testo: almeno il 4% di inchiostro e due vicine come lei (un
  // granello di polvere o un riflesso isolato non conta).
  const piena = (i) => inchiostro[i] >= CELLA * CELLA * 0.04;
  const testo = new Uint8Array(cw * ch);
  for (let cy = 0; cy < ch; cy++) {
    for (let cx = 0; cx < cw; cx++) {
      if (!piena(cy * cw + cx)) {
        continue;
      }
      let vicine = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const x = cx + dx;
          const y = cy + dy;
          if ((dx || dy) && x >= 0 && y >= 0 && x < cw && y < ch && piena(y * cw + x)) {
            vicine++;
          }
        }
      }
      testo[cy * cw + cx] = vicine >= 2 ? 1 : 0;
    }
  }
  // Il tratto piu' ricco di testo di un profilo, scavalcando i vuoti piccoli.
  const tratto = (profilo, vuoto) => {
    let migliore = null;
    let corrente = null;
    let buco = 0;
    profilo.forEach((n, i) => {
      if (n >= 2) {
        if (!corrente) {
          corrente = { da: i, a: i, somma: 0 };
        }
        corrente.a = i;
        corrente.somma += n;
        buco = 0;
      } else if (corrente && ++buco > vuoto) {
        if (!migliore || corrente.somma > migliore.somma) {
          migliore = corrente;
        }
        corrente = null;
        buco = 0;
      }
    });
    if (corrente && (!migliore || corrente.somma > migliore.somma)) {
      migliore = corrente;
    }
    return migliore;
  };
  const vuoto = Math.round(Math.max(cw, ch) * 0.07);
  const righe = new Array(ch).fill(0);
  for (let cy = 0; cy < ch; cy++) {
    for (let cx = 0; cx < cw; cx++) {
      righe[cy] += testo[cy * cw + cx];
    }
  }
  const ty = tratto(righe, vuoto);
  if (!ty) {
    return null;
  }
  const colonne = new Array(cw).fill(0);
  for (let cy = ty.da; cy <= ty.a; cy++) {
    for (let cx = 0; cx < cw; cx++) {
      colonne[cx] += testo[cy * cw + cx];
    }
  }
  const tx = tratto(colonne, vuoto);
  if (!tx) {
    return null;
  }
  // Margine: una cella e mezza piu' il 2,5% della foto, per non tagliare bordi e righe
  // della MRZ appena fuori dalla zona trovata.
  const f = img.width / w;
  const mx = CELLA * 1.5 + w * 0.025;
  const my = CELLA * 1.5 + h * 0.025;
  const x0 = Math.max(0, tx.da * CELLA - mx);
  const y0 = Math.max(0, ty.da * CELLA - my);
  const x1 = Math.min(w, (tx.a + 1) * CELLA + mx);
  const y1 = Math.min(h, (ty.a + 1) * CELLA + my);
  // Zona troppo piccola (un pezzo di testo sul tavolo) o quasi tutta la foto:
  // non conviene ritagliare.
  const quota = ((x1 - x0) * (y1 - y0)) / (w * h);
  if (quota > 0.6 || quota < 0.04) {
    return null;
  }
  return { x: Math.round(x0 * f), y: Math.round(y0 * f), w: Math.round((x1 - x0) * f), h: Math.round((y1 - y0) * f) };
}

/**
 * Dove sta il documento su un fondo con molta trama (tovaglia, tessuto,
 * legno venato), quando trovaDocumento non ritaglia.
 *
 * Nella maschera del testo la trama del fondo accende tutte le celle, e
 * l'OCR sulla foto intera legge solo rumore. Il documento invece ha sempre
 * zone lisce (margini, spazi tra le righe, fondo della tessera): le colonne
 * e le righe del fondo non hanno quasi nessuna cella chiara, quelle del
 * documento parecchie. Si ritaglia il tratto di colonne e di righe con celle
 * chiare. Vale solo se il bordo della foto e' davvero trama: su un fondo
 * liscio le celle chiare sono ovunque e il ritaglio sarebbe a caso.
 *
 * @returns {{x, y, w, h}|null} zona in pixel della foto, null se non serve
 */
export function trovaDocumentoSuTrama(amb, img) {
  const maschera = mascheraTesto(amb, img, 700);
  const { width: w, height: h } = maschera;
  const d = maschera.getContext("2d").getImageData(0, 0, w, h).data;
  const CELLA = 14;
  const cw = Math.ceil(w / CELLA);
  const ch = Math.ceil(h / CELLA);
  const inchiostro = new Float32Array(cw * ch);
  const punti = new Float32Array(cw * ch);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = Math.floor(y / CELLA) * cw + Math.floor(x / CELLA);
      punti[i]++;
      if (d[(y * w + x) * 4] < 128) {
        inchiostro[i]++;
      }
    }
  }
  const quota = (cx, cy) => inchiostro[cy * cw + cx] / punti[cy * cw + cx];
  const chiara = (cx, cy) => quota(cx, cy) < 0.05;
  // Il bordo della foto (due celle di spessore) non deve avere quasi nessuna
  // cella chiara: sono le celle chiare a dire dov'e' il documento. Un tessuto
  // ne ha 0-2%, un fondo liscio (o le bande nere di uno screenshot) quasi la
  // meta' o piu'. Quanta trama ci sia non conta: un tessuto un po' sfocato ne
  // ha meno, ma resta senza celle chiare.
  let bordo = 0;
  let chiare = 0;
  for (let cy = 0; cy < ch; cy++) {
    for (let cx = 0; cx < cw; cx++) {
      if (cx < 2 || cy < 2 || cx >= cw - 2 || cy >= ch - 2) {
        bordo++;
        if (chiara(cx, cy)) {
          chiare++;
        }
      }
    }
  }
  if (chiare > bordo * 0.1) {
    return null;
  }
  // Il tratto piu' lungo di un profilo sopra la soglia, scavalcando due vuoti.
  const tratto = (profilo, soglia) => {
    let migliore = null;
    let corrente = null;
    let buco = 0;
    profilo.forEach((n, i) => {
      if (n >= soglia) {
        corrente = corrente || { da: i, a: i };
        corrente.a = i;
        buco = 0;
      } else if (corrente && ++buco > 2) {
        if (!migliore || corrente.a - corrente.da > migliore.a - migliore.da) {
          migliore = corrente;
        }
        corrente = null;
        buco = 0;
      }
    });
    if (corrente && (!migliore || corrente.a - corrente.da > migliore.a - migliore.da)) {
      migliore = corrente;
    }
    return migliore;
  };
  const colonne = new Array(cw).fill(0);
  for (let cx = 0; cx < cw; cx++) {
    for (let cy = 0; cy < ch; cy++) {
      colonne[cx] += chiara(cx, cy) ? 1 : 0;
    }
  }
  const tx = tratto(colonne, Math.max(2, ch * 0.08));
  if (!tx) {
    return null;
  }
  const righe = new Array(ch).fill(0);
  for (let cy = 0; cy < ch; cy++) {
    for (let cx = tx.da; cx <= tx.a; cx++) {
      righe[cy] += chiara(cx, cy) ? 1 : 0;
    }
  }
  const ty = tratto(righe, Math.max(2, (tx.a - tx.da + 1) * 0.08));
  if (!ty) {
    return null;
  }
  // Margine di una cella: il bordo del documento sta nella cella di trama accanto.
  const x0 = Math.max(0, (tx.da - 1) * CELLA);
  const y0 = Math.max(0, (ty.da - 1) * CELLA);
  const x1 = Math.min(w, (tx.a + 2) * CELLA);
  const y1 = Math.min(h, (ty.a + 2) * CELLA);
  // Proporzioni da documento (tessera 1,59, pagina di passaporto 1,42) e
  // una parte ragionevole della foto: altrimenti e' una macchia del fondo.
  const lungo = Math.max(x1 - x0, y1 - y0);
  const corto = Math.min(x1 - x0, y1 - y0);
  const parte = ((x1 - x0) * (y1 - y0)) / (w * h);
  if (lungo / corto < 1.15 || lungo / corto > 2.1 || parte < 0.1 || parte > 0.9) {
    return null;
  }
  const f = img.width / w;
  return { x: Math.round(x0 * f), y: Math.round(y0 * f), w: Math.round((x1 - x0) * f), h: Math.round((y1 - y0) * f) };
}

/**
 * Verso e inclinazione del testo nella foto, stimati una volta sola
 * all'inizio: il documento puo' essere orizzontale in una foto verticale
 * (telefono tenuto dritto) o girato di 90 gradi, e quasi sempre un po' storto.
 * Si provano piccoli angoli sulla maschera del testo, com'e' e girata di 90
 * gradi: vince quello con le righe di testo piu' nette. Prima ogni grado, poi
 * mezzo grado attorno al migliore. Capovolto o no (0/180, 90/270) non si
 * distingue qui: lo dice poi la lettura.
 */
export function stimaOrientamento(amb, img) {
  const maschera = mascheraTesto(amb, img, 700);
  let migliore = { gradi: 0, inclinazione: 0, punti: -1 };
  const prova = (base, gradi, inclinazione) => {
    const punti = puntiRighe(inclina(amb, base, inclinazione));
    if (punti > migliore.punti) {
      migliore = { gradi, inclinazione, punti };
    }
  };
  const basi = { 0: maschera, 90: ruota(amb, maschera, 90) };
  for (const gradi of [0, 90]) {
    for (let inclinazione = -12; inclinazione <= 12; inclinazione += 1) {
      prova(basi[gradi], gradi, inclinazione);
    }
  }
  const { gradi, inclinazione } = migliore;
  prova(basi[gradi], gradi, inclinazione - 0.5);
  prova(basi[gradi], gradi, inclinazione + 0.5);
  return migliore;
}

/**
 * Terza lettura, per le foto storte: si ritaglia la fascia della MRZ, la si
 * raddrizza e la si rilegge da capo come se fosse una foto a se'.
 */
async function rileggiRaddrizzata(amb, originale, candidate, fattoreRicerca) {
  const altezza = mediana(candidate.map((r) => Math.min(r.y1 - r.y0, (r.x1 - r.x0) / 10))) / fattoreRicerca;
  const y0 = Math.min(...candidate.map((r) => r.y0)) / fattoreRicerca;
  const y1 = Math.max(...candidate.map((r) => r.y1)) / fattoreRicerca;
  // La riga trovata puo' essere la prima, l'ultima o quella in mezzo: si
  // prende abbastanza sopra e sotto per avere tutte le righe della MRZ.
  const r = { x: 0, y: Math.max(0, Math.floor(y0 - altezza * 5)), w: originale.width, h: 0 };
  r.h = Math.min(originale.height, Math.ceil(y1 + altezza * 5)) - r.y;
  const zona = raddrizza(amb, ritaglia(amb, originale, r, 1));
  const ricerca = grigio(scala(amb, zona, LATO_RICERCA, true));
  const fattore = ricerca.width / zona.width;
  const righe = await amb.ocr(ricerca, { psm: 3 });
  const nuove = righeMrz(righe);
  if (!nuove.length) {
    return null;
  }
  const prima = leggiMrz(nuove.map((x) => x.testo));
  if (prima && prima.valida) {
    return prima;
  }
  const seconda = await rileggiRighe(amb, zona, nuove, fattore);
  return punti(seconda) > punti(prima) ? seconda : prima;
}

/**
 * Dove sono state trovate le righe della MRZ letta (righe dell'OCR e scala
 * della ricerca), per poterle rileggere senza cercarle di nuovo. Fuori dal
 * risultato: la lettura va al backend e nella pagina, questi dati no.
 */
const posizioniMrz = new WeakMap();

/**
 * Cerca e legge la MRZ, provando anche il documento capovolto o girato.
 *
 * @returns {Promise<object|null>} la lettura migliore trovata
 */
export async function cercaMrz(amb, originale, ordine) {
  let migliore = null;
  const avanza = amb.avanzamento || (() => {});
  const tutti = ordine || versi(originale);
  for (const [i, gradi] of tutti.entries()) {
    // Ogni verso provato occupa un pezzo del tratto 10-50%.
    avanza("Lettura della zona MRZ", 10 + (40 * i) / tutti.length, 10 + (40 * (i + 1)) / tutti.length);
    const img = gradi ? ruota(amb, originale, gradi) : originale;
    // Le foto piccole si ingrandiscono: con fronte e retro nella stessa foto
    // le righe della MRZ sono di pochi pixel.
    const ricerca = grigio(scala(amb, img, LATO_RICERCA, Math.max(img.width, img.height) < 1200));
    const fattore = ricerca.width / img.width;
    const righe = await amb.ocr(ricerca, { psm: 3 });
    const candidate = righeMrz(righe);
    // Il documento e' in questo verso solo se almeno una riga ha i riempitivi
    // della MRZ: in una foto girata l'OCR legge comunque righe di lettere e
    // cifre, e senza questa prova ci si fermerebbe al verso sbagliato.
    const riempitivi = (r) => (pulisciRiga(r.testo).match(/</g) || []).length;
    if (!candidate.some((r) => riempitivi(r) >= 5)) {
      continue;
    }
    const prima = leggiMrz(candidate.map((r) => r.testo));
    if (punti(prima) > punti(migliore)) {
      migliore = prima;
    }
    const frammenti = frammentiMrz(righe, candidate);
    const posizione = { righe: frammenti.length > candidate.length ? frammenti : candidate, fattore };
    if (!migliore || !migliore.valida) {
      const seconda = await rileggiRighe(amb, img, posizione.righe, fattore);
      if (punti(seconda) > punti(migliore)) {
        migliore = seconda;
      }
    }
    if (!migliore || !migliore.valida) {
      const terza = await rileggiRaddrizzata(amb, img, candidate, fattore);
      if (punti(terza) > punti(migliore)) {
        migliore = terza;
      }
    }
    // La MRZ c'e' in questo verso: girare ancora il documento non serve.
    if (migliore) {
      // Il verso serve poi a leggere la parte stampata della stessa foto.
      migliore.gradi = gradi;
      posizioniMrz.set(migliore, posizione);
    }
    break;
  }
  return migliore;
}

/**
 * Cognome e nome della MRZ corretti con il codice fiscale.
 *
 * I nomi sono l'unica parte della MRZ senza cifra di controllo: su una foto
 * un po' sfocata l'OCR scambia lettere simili (N e W, N e M) e il resto torna
 * lo stesso ("JERWIFER"). Il codice fiscale ha le consonanti di cognome e
 * nome. Se non tornano, si rileggono le righe della MRZ con l'altro bianco e
 * nero e con altre altezze. Il codice fiscale da solo non basta a scegliere:
 * del nome controlla tre consonanti, e anche "JERNIFPER" torna (J, N, F).
 * Ogni lettura che torna e' allora un voto, e vince la prima che ne ha due:
 * gli errori dell'OCR cambiano da una lettura all'altra, il nome giusto si
 * ripete. Se nessuna arriva a due voti la MRZ resta com'e': il codice fiscale
 * e' forse di un'altra persona, e lo dice poi il backend.
 *
 * @param {object} img la foto nel verso in cui la MRZ e' stata letta
 * @returns {Promise<boolean>} true se ha corretto qualcosa
 */
export async function correggiNomeMrz(amb, img, mrz, codiceFiscale) {
  const posizione = posizioniMrz.get(mrz);
  if (!posizione || !codiceFiscale || !codiceFiscale.valido) {
    return false;
  }
  const cf = codiceFiscale.codice;
  const campi = {
    cognome: (c) => !!c && !/\d/.test(c) && codiceCognome(c) === cf.substring(0, 3),
    nome: (n) => !!n && !/\d/.test(n) && codiceNome(n) === cf.substring(3, 6)
  };
  const daCorreggere = Object.keys(campi).filter((k) => !campi[k](mrz[k]));
  if (!daCorreggere.length) {
    return false;
  }
  // Voti per campo; la lettura della MRZ conta come una delle letture.
  const voti = { cognome: new Map(), nome: new Map() };
  const vota = (lettura) => {
    for (const k of Object.keys(campi)) {
      if (lettura && campi[k](lettura[k])) {
        voti[k].set(lettura[k], (voti[k].get(lettura[k]) || 0) + 1);
      }
    }
  };
  const eletto = (k) => [...voti[k].entries()].find(([, n]) => n >= 2);
  vota(mrz);
  const varianti = [{ soglia: "adattiva" }, { altezzaRiga: 36, soglia: "adattiva" }, { altezzaRiga: 54, soglia: "adattiva" }, { altezzaRiga: 36 }, { altezzaRiga: 54 }];
  for (const variante of varianti) {
    vota(await rileggiRighe(amb, img, posizione.righe, posizione.fattore, variante));
    if (daCorreggere.every(eletto)) {
      break;
    }
  }
  let corretto = false;
  for (const k of daCorreggere) {
    const e = eletto(k);
    if (e) {
      mrz[k] = e[0];
      corretto = true;
    }
  }
  return corretto;
}

// ___________________________ DOCUMENTO ___________________________

/**
 * Legge una foto del documento.
 *
 * @param {object} amb ambiente (vedi in testa al file)
 * @param {CanvasImageSource} immagine foto gia' caricata
 * @returns {Promise<{mrz: object|null, codiceFiscale: object|null, patenteUsa: object|null, codici: object[]}>}
 */
export async function leggiImmagine(amb, immagine) {
  const avanza = amb.avanzamento || (() => {});
  // Solo il documento, a piena risoluzione, se occupa una parte della foto.
  const zonaTesto = trovaDocumento(amb, immagine);
  const zonaTrama = zonaTesto ? null : trovaDocumentoSuTrama(amb, immagine);
  const zona = zonaTesto || zonaTrama;
  let originale = zona ? ritaglia(amb, immagine, zona, Math.min(1, LATO_MASSIMO / Math.max(zona.w, zona.h))) : scala(amb, immagine, LATO_MASSIMO);
  // Foto sfocata: lo si dice subito, la lettura dura decine di secondi. Si
  // legge lo stesso: la soglia e' tarata su poche foto, e una foto appena
  // sotto puo' ancora dare qualcosa. Si misura prima della cornice, che ha
  // bordi netti suoi.
  const netta = nitidezza(amb, originale);
  if (amb.sfocata && netta < NITIDEZZA_MINIMA) {
    amb.sfocata(netta);
  }
  // Fondo scuro messo da noi: vedi cornice(). Non sul ritaglio da un fondo a
  // trama: li' il bordo di tessuto rimasto, contro il nero, peggiora la lettura.
  if (!zonaTrama) {
    originale = cornice(amb, originale);
  }

  // Percentuale stimata: codici a barre 0-10, MRZ 10-50, testo 50-95; il
  // resto e' la risposta del server. Le fasi saltate fanno solo avanzare.
  avanza("Ricerca dei codici a barre", 0, 10);
  let codici = [];
  try {
    codici = await amb.barcode(originale);
  } catch (e) {
    // Un errore del lettore di codici a barre non deve fermare la MRZ.
    codici = [];
  }
  let codiceFiscale = null;
  let patenteUsa = null;
  for (const c of codici) {
    codiceFiscale = codiceFiscale || trovaCodiceFiscale(c.testo);
    if (c.formato === "PDF417") {
      patenteUsa = patenteUsa || leggiAamva(c.testo);
    }
  }

  // Foto raddrizzata: testo orizzontale e non inclinato. I codici a barre si
  // cercano sulla foto com'e', il lettore li trova in qualsiasi verso.
  const orientamento = stimaOrientamento(amb, originale);
  let dritta = orientamento.gradi ? ruota(amb, originale, orientamento.gradi) : originale;
  // Sotto i 2,5 gradi l'OCR legge bene da solo, e raddrizzare ricampiona la
  // foto sfocandola un po': sulle foto gia' difficili (riflessi, compresse)
  // peggiora la lettura.
  if (Math.abs(orientamento.inclinazione) >= 2.5) {
    // Gli angoli scoperti prendono il colore medio della foto: il bianco
    // falserebbe la regolazione del contrasto delle foto scure.
    dritta = inclina(amb, dritta, orientamento.inclinazione, coloreMedio(amb, dritta));
  }
  // Prima il verso stimato e il capovolto, poi gli altri due per sicurezza.
  const ordine = [0, 180, 90, 270];

  let mrz = null;
  if (!patenteUsa) {
    mrz = await cercaMrz(amb, dritta, ordine);
    // Quasi nessuna cifra di controllo torna: e' una lettura sbagliata (riflesso,
    // fondino), e i suoi dati finirebbero nella scheda come se fossero buoni.
    if (mrz && !mrz.valida && punti(mrz) <= 1) {
      mrz = null;
    }
    // Fondino letto come MRZ, con due cifre di controllo che tornano per
    // caso: il tipo di documento non esiste (passaporto P, carte I/A/C, visto
    // V) o nel nome ci sono cifre. Senza la composita non ci si fida.
    const cifreNelNome = (m) => /\d/.test((m.cognome || "") + (m.nome || ""));
    if (mrz && !mrz.valida && !mrz.controlli.composito && (!/^[PIACV]/.test(mrz.tipo || "") || cifreNelNome(mrz))) {
      mrz = null;
    }
    // Un nome con cifre e' letto male anche se il resto torna.
    if (mrz && cifreNelNome(mrz)) {
      mrz.cognome = "";
      mrz.nome = "";
    }
    // Un numero con "<" in mezzo e la cifra di controllo sbagliata e' letto
    // male: meglio il campo vuoto da scrivere che un numero sbagliato.
    if (mrz && !mrz.controlli.numero && mrz.numero.includes("<")) {
      mrz.numero = "";
    }
  }

  // Retro della CIE senza codice fiscale: il codice a barre non si e' letto
  // sulla foto com'e' (storta, scura, riflessi). Si riprova sulla foto
  // raddrizzata e su una copia in grigi a contrasto pieno.
  if (mrz && mrz.formato === "TD1" && mrz.emittente === "ITA" && !codiceFiscale) {
    for (const prova of [() => dritta, () => grigio(scala(amb, dritta, 2400, true))]) {
      try {
        for (const c of await amb.barcode(prova())) {
          codiceFiscale = codiceFiscale || trovaCodiceFiscale(c.testo);
        }
      } catch (e) {
        // si continua senza codice a barre
      }
      if (codiceFiscale) {
        break;
      }
    }
  }

  // Testo stampato: senza MRZ puo' essere una patente UE o il fronte della
  // CIE; con la MRZ di un documento italiano o di un passaporto ci sono dati
  // che la MRZ non ha (comune e data di rilascio, luogo di nascita, codice
  // fiscale stampato, residenza).
  let patenteUe = null;
  let viz = null;
  // Dei passaporti stranieri la parte stampata non da' nulla che serva alla
  // questura: si salta, e si risparmiano alcuni secondi.
  const testoUtile = !mrz || mrz.emittente === "ITA";
  if (!patenteUsa && amb.ocrTesto && testoUtile) {
    // Quando fermarsi: con la MRZ servono solo i dati che lei non ha.
    const completo = (patente, viz) => {
      if (mrz && mrz.formato === "TD1") {
        return !!(viz && (codiceFiscale || viz.codiceFiscale) && viz.indirizzo);
      }
      if (mrz) {
        return !!(viz && viz.comuneRilascio && viz.luoghi.length && viz.date.length >= 3);
      }
      if (viz && viz.cie) {
        const f = viz.fronte || {};
        return !!(viz.comuneRilascio && viz.luoghi.length && viz.date.length >= 3 && f.cognome && f.nome && f.numero);
      }
      return puntiTesto(patente, viz) >= 10;
    };
    const letto = await leggiTestoStampato(amb, dritta, mrz ? [mrz.gradi || 0] : ordine, completo);
    patenteUe = mrz ? null : letto.patenteUe;
    viz = letto.viz;
    if (!codiceFiscale && viz && viz.codiceFiscale) {
      codiceFiscale = viz.codiceFiscale;
    }
  }

  // Nomi della MRZ (senza cifra di controllo) contro il codice fiscale.
  if (mrz && codiceFiscale) {
    avanza("Controllo del nome con il codice fiscale", 95, 98);
    await correggiNomeMrz(amb, mrz.gradi ? ruota(amb, dritta, mrz.gradi) : dritta, mrz, codiceFiscale);
  }
  return { mrz, codiceFiscale, patenteUsa, patenteUe, viz, codici, nitidezza: netta };
}

/** Copia in grigi del solo canale rosso: timbri e scritte rosse o rosa spariscono. */
function canaleRosso(amb, sorgente) {
  const c = scala(amb, sorgente, Math.max(sorgente.width, sorgente.height), false);
  const ctx = c.getContext("2d");
  const img = ctx.getImageData(0, 0, c.width, c.height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    d[i + 1] = d[i + 2] = d[i];
  }
  ctx.putImageData(img, 0, 0);
  return grigio(c);
}

/** Punteggio di una lettura del testo: quanti dati utili ha dato. */
function puntiTesto(patente, viz) {
  let p = patente ? patente.campiLetti * 2 : 0;
  if (viz) {
    p += (viz.comuneRilascio ? 2 : 0) + viz.luoghi.length * 2 + viz.date.length + (viz.codiceFiscale ? 3 : 0) + (viz.indirizzo ? 1 : 0);
    if (viz.fronte) {
      p += Object.values(viz.fronte).filter(Boolean).length * 2;
    }
  }
  return p;
}

/**
 * Legge il testo stampato (patente UE e parte stampata dei documenti),
 * provando i versi indicati. Le foto piccole si ingrandiscono: il testo e'
 * minuto e l'OCR lo legge meglio con caratteri piu' grandi.
 *
 * Nessuna impaginazione dell'OCR va bene sempre: su due immagini quasi
 * identiche la stessa a volte trova i blocchi di testo e a volte no. Si
 * prova "colonna di testo" (psm 4, veloce), l'impaginazione automatica
 * (psm 3) e "testo sparso" (psm 11, la piu' lenta ma la piu' stabile).
 * Prima con la soglia adattiva dell'OCR: sulla CIE il fondino guilloche
 * chiaro, con la soglia unica, diventa nero quanto le scritte e l'OCR non
 * trova piu' le righe. Poi sulla foto in grigi e sulla maschera del testo
 * (toglie fondini e sfumature), e sul solo canale rosso, che toglie timbri e
 * scritte colorate stampate sopra.
 * Ogni prova trova pezzi diversi: si uniscono quelli dello stesso verso.
 * Ci si ferma appena la lettura e' completa (`completo`); un verso che alla
 * prima prova da' solo testo senza senso (foto girata) non si insiste.
 */
export async function leggiTestoStampato(amb, originale, versi, completo = (patente, viz) => puntiTesto(patente, viz) >= 10) {
  let migliore = { patenteUe: null, viz: null, punti: 0 };
  const avanza = amb.avanzamento || (() => {});
  const PROVE = 9;
  // Ogni prova (verso per tentativo) occupa un pezzo del tratto 50-95%.
  const passi = versi.length * PROVE;
  for (const [v, gradi] of versi.entries()) {
    const img = gradi ? ruota(amb, originale, gradi) : originale;
    const ingrandisci = Math.max(img.width, img.height) < 1400;
    const lato = ingrandisci ? 2000 : LATO_RICERCA;
    // Ogni preparazione parte da una copia nuova: grigio() cambia il canvas.
    const prove = [
      { pagina: () => grigio(scala(amb, img, lato, ingrandisci)), psm: 4, soglia: "adattiva" },
      { pagina: () => grigio(scala(amb, img, lato, ingrandisci)), psm: 3, soglia: "adattiva" },
      { pagina: () => grigio(scala(amb, img, lato, ingrandisci)), psm: 11, soglia: "adattiva" },
      { pagina: () => grigio(scala(amb, img, lato, ingrandisci)), psm: 4 },
      { pagina: () => mascheraTesto(amb, img, lato), psm: 4 },
      { pagina: () => grigio(scala(amb, img, lato, ingrandisci)), psm: 3 },
      { pagina: () => mascheraTesto(amb, img, lato), psm: 11 },
      { pagina: () => grigio(scala(amb, img, lato, ingrandisci)), psm: 11 },
      { pagina: () => canaleRosso(amb, scala(amb, img, lato, ingrandisci)), psm: 11 }
    ];
    // Quello che hanno trovato le prove di questo verso, insieme.
    let patenteVerso = null;
    let vizVerso = null;
    for (const [n, prova] of prove.entries()) {
      const k = v * PROVE + n;
      avanza("Lettura del testo stampato", 50 + (45 * k) / passi, 50 + (45 * (k + 1)) / passi);
      const righe = (await amb.ocrTesto(prova.pagina(), { psm: prova.psm, soglia: prova.soglia })).map((r) => r.testo);
      const patenteUe = leggiPatenteUe(righe);
      const viz = leggiViz(righe);
      if (patenteUe && (!patenteVerso || patenteUe.campiLetti > patenteVerso.campiLetti)) {
        patenteVerso = patenteUe;
      }
      vizVerso = unisciViz([vizVerso, viz]);
      const p = puntiTesto(patenteVerso, vizVerso);
      if (p > migliore.punti) {
        migliore = { patenteUe: patenteVerso, viz: vizVerso, punti: p };
      }
      if (completo(migliore.patenteUe, migliore.viz)) {
        return migliore;
      }
      // Verso sbagliato: poche parole vere e nessun dato. Si passa al prossimo.
      const parole = righe.join(" ").match(/[A-Za-zÀ-ÿ]{4,}/g) || [];
      if (n === 0 && p === 0 && parole.length < 6) {
        break;
      }
    }
  }
  return migliore;
}

/** Solo patente UE (per compatibilita' con i test): vedi leggiTestoStampato. */
export async function cercaPatente(amb, originale) {
  return (await leggiTestoStampato(amb, originale, versi(originale))).patenteUe;
}

export default { leggiImmagine, cercaMrz, cercaPatente, leggiTestoStampato };
